import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, BookOpen, Clock, Users, Play, Filter, Loader2,
  CheckCircle2, Lock, Send, GraduationCap, Calendar
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import BookSessionDialog from "../../components/shared/BookSessionDialog";

const typeLabels = { "scorm_1.2": "eLearning", "scorm_2004": "eLearning", ilt: "ILT", video: "Video", document: "Document" };

const LearnerCatalogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [courses, setCourses] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [requestedIds, setRequestedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  const [requestDialog, setRequestDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [bookCourse, setBookCourse] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, enrollmentsRes, requestsRes] = await Promise.all([
        api.get("/courses?is_published=true"),
        api.get("/my/learning?page=1&page_size=500"),
        api.get("/my/access-requests").catch(() => ({ data: [] })),
      ]);
      const allCourses = coursesRes.data || [];
      setCourses(allCourses);
      
      const enrolled = new Set((enrollmentsRes.data?.courses || []).map(c => c.course_id));
      setEnrolledIds(enrolled);
      
      const requested = new Set((requestsRes.data || []).filter(r => r.status === "pending").map(r => r.course_id));
      setRequestedIds(requested);
      
      const cats = [...new Set(allCourses.map(c => c.category).filter(Boolean))];
      setCategories(cats);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredCourses = courses.filter(c => {
    if (search && !c.title?.toLowerCase().includes(search.toLowerCase()) && !c.description?.toLowerCase().includes(search.toLowerCase())) return false;
    if (categoryFilter !== "all" && c.category !== categoryFilter) return false;
    return true;
  });

  const requestAccess = async () => {
    if (!requestDialog) return;
    setRequesting(true);
    try {
      await api.post(`/courses/${requestDialog.id}/request-access`, { reason });
      toast.success("Access request submitted");
      setRequestedIds(prev => new Set([...prev, requestDialog.id]));
      setRequestDialog(null);
      setReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    } finally { setRequesting(false); }
  };

  const selfEnroll = async (courseId) => {
    try {
      await api.post("/enrollments", { course_id: courseId });
      toast.success("Successfully enrolled!");
      setEnrolledIds(prev => new Set([...prev, courseId]));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enroll");
    }
  };

  return (
    <AdminLayout title="Course Catalog" variant={variant}>
      <div className="space-y-6" data-testid="learner-catalog-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
            Course Catalog
          </h1>
          <p className="text-muted-foreground text-sm">Browse and enroll in available courses</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-9" data-testid="catalog-search" />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="category-filter">
              <Filter className="w-3 h-3 mr-1" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Course Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground">No courses found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCourses.map((course, i) => {
              const isEnrolled = enrolledIds.has(course.id);
              const isRequested = requestedIds.has(course.id);
              return (
                <motion.div key={course.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card className="overflow-hidden hover:border-primary/20 transition-colors h-full flex flex-col" data-testid={`catalog-course-${course.id}`}>
                    {/* Thumbnail */}
                    <div className="h-32 relative" style={course.thumbnail_url ? {
                      background: `linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.4)), url(${course.thumbnail_url}) center/cover`
                    } : { background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)/0.7))" }}>
                      <div className="absolute top-2 left-2 flex gap-1">
                        <Badge className="bg-black/40 text-white border-0 text-[10px]">
                          {typeLabels[course.course_type] || course.course_type}
                        </Badge>
                      </div>
                      {isEnrolled && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-green-500/90 text-white border-0 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Enrolled
                          </Badge>
                        </div>
                      )}
                      {isRequested && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-amber-500/90 text-white border-0 text-[10px]">
                            <Clock className="w-3 h-3 mr-1" /> Requested
                          </Badge>
                        </div>
                      )}
                    </div>

                    <CardContent className="p-4 flex-1 flex flex-col">
                      <h4 className="font-medium text-sm line-clamp-2 mb-1">{course.title}</h4>
                      <p className="text-xs text-muted-foreground line-clamp-2 flex-1">{course.description?.slice(0, 100)}</p>
                      <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
                        {course.duration_hours > 0 && (
                          <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {course.duration_hours}h</span>
                        )}
                        <span className="flex items-center gap-0.5"><GraduationCap className="w-3 h-3" /> {course.category}</span>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        {course.course_type === "ilt" && !isEnrolled && (
                          <Button size="sm" variant="outline" className="w-full gap-1 border-primary/30 text-primary" onClick={() => setBookCourse(course)} data-testid={`book-ilt-${course.id}`}>
                            <Calendar className="w-3.5 h-3.5" /> Book Session
                          </Button>
                        )}
                        {isEnrolled ? (
                          <Button size="sm" className="w-full" onClick={() => navigate(`/course/${course.id}`)} data-testid={`goto-course-${course.id}`}>
                            <Play className="w-3.5 h-3.5 mr-1" /> Go to Course
                          </Button>
                        ) : isRequested ? (
                          <Button size="sm" variant="outline" className="w-full" disabled>
                            <Clock className="w-3.5 h-3.5 mr-1" /> Request Pending
                          </Button>
                        ) : course.self_enrollment_enabled !== false ? (
                          <Button size="sm" className="w-full" onClick={() => selfEnroll(course.id)} data-testid={`enroll-${course.id}`}>
                            <BookOpen className="w-3.5 h-3.5 mr-1" /> Enroll Now
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="w-full" onClick={() => setRequestDialog(course)} data-testid={`request-${course.id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" /> Request Access
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Request Access Dialog */}
        <Dialog open={!!requestDialog} onOpenChange={(v) => { if (!v) setRequestDialog(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" /> Request Course Access
              </DialogTitle>
              <DialogDescription>
                Request access to "{requestDialog?.title}"
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Why do you need access to this course?"
                className="mt-1"
                rows={3}
                data-testid="request-reason"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRequestDialog(null)}>Cancel</Button>
              <Button onClick={requestAccess} disabled={requesting} data-testid="submit-request-btn">
                {requesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Book ILT Session Dialog */}
        <BookSessionDialog
          open={!!bookCourse}
          onOpenChange={(v) => { if (!v) setBookCourse(null); }}
          courseId={bookCourse?.id || ""}
          courseTitle={bookCourse?.title || ""}
        />
      </div>
    </AdminLayout>
  );
};

export default LearnerCatalogPage;
